<template>
  <div>
    <label class="number">
      <div v-if="title" class="number__label">
        <span class="number__title">{{ title }}</span>
        <span v-if="textTooltip" class="number__tooltip">
          <span class="number__tooltip-hint">{{ textTooltip }}</span>
        </span>
      </div>
      <input
        class="number__input"
        type="number"
        v-bind:disabled="disabled"
        v-bind:class="[appearanceClass, { currency: currency }]"
        v-bind:max="max"
        v-bind:min="min"
        v-bind:name="name"
        v-bind:required="required"
        v-bind:step="step"
        v-on:blur="onBlur"
        v-on:focus="onFocus"
        v-on:input="onInput"
        v-on:mousewheel="onMousewheel"
        v-mask="mask"
        v-model="inputValue"
      />
    </label>
    <div
      v-for="(error, index) in violations"
      v-bind:key="index"
      class="number__validate-error"
    >{{ error }}</div>
  </div>

</template>
<script>

/**
 * @description Название события фокуса.
 * @type {string}
 */
const NAME_EVENT_FOCUS = 'focus';

/**
 * @description Название события ввода.
 * @type {string}
 */
const NAME_EVENT_INPUT = 'input';

/**
 * @description Название события потери фокуса.
 * @type {string}
 */
const NAME_EVENT_BLUR = 'blur';

/**
 * @description Числовой ввод.
 * @type {Vue}
 */
export default {
  data() {
    return {
      /**
       * @description Класс оформления.
       * @type {string}
       */
      appearanceClass: this.withoutNativeControls ? 'without-controls' : '',
      /**
       * @description Значение поля.
       * @type {string}
       */
      inputValue: this.value ? this.value.toString() : '',
    };
  },
  methods: {
    /**
     * @description Форматировать значение.
     * @param event
     */
    formattedValue(event) {
      let { value } = event.target;
      if (value === '' && this.allowedNull) {
        this.inputValue = '';
        this.$emit(NAME_EVENT_INPUT, '');
        return;
      }
      value = value.replace(/[^0-9-]/g, '');
      if (value.indexOf('-') > 0) {
        value = value.replace('-', '');
      }
      if (this.nativeMinMaxValidation !== true) {
        if (this.min) {
          value = Math.max(Number(value), Number(this.min));
        }
        if (this.max) {
          value = Math.min(Number(value), Number(this.max));
        }
      }
      this.inputValue = value;
    },
    /**
     * @description Обработать фокус.
     * @returns {void}
     */
    onFocus() {
      this.$emit(NAME_EVENT_FOCUS);
    },
    /**
     * @description Обработать ввод.
     * @param {InputEvent} event Событие.
     * @returns {void}
     */
    onInput(event) {
      this.formattedValue(event);
      this.$emit(NAME_EVENT_INPUT, this.inputValue);
    },
    /**
     * @description Обработать потерю фокуса.
     * @param event
     */
    onBlur(event) {
      this.formattedValue(event);
      this.$emit(NAME_EVENT_BLUR, this.inputValue);
    },
    /**
     * @description Обработать колесико мыши.
     * @param event
     */
    onMousewheel(event) {
      event.preventDefault();
    },
  },
  name: 'InputNumber',
  props: {
    /**
     * @description Максимальное значение.
     * @type {(String|Boolean)}
     */
    max: {
      default: () => false,
      type: [Boolean, String, Number],
    },
    /**
     * @description Минимальное значение.
     * @type {(boolean|number|string)}
     */
    min: { default: () => false, type: [Boolean, String, Number] },
    /**
     * @description Уникальное имя элемента формы.
     * @type {String}
     */
    name: { default: () => false, type: [String, Boolean] },
    /**
     * @description Поле обязательно для заполнения.
     * @type {boolean|string}
     */
    required: { default: () => false, type: [String, Boolean] },
    /**
     * @description Шаг изменения значения.
     * @type {string}
     */
    step: { default: () => false, type: [String, Boolean] },
    /**
     * @description Признак валюты.
     * @type {boolean}
     */
    currency: { default: () => false, type: Boolean },
    /**
     * @description Заголовок.
     * @type {string}
     */
    title: { default: '', type: String },
    /**
     * @description Текст для тултипа.
     * @type {string}
     */
    textTooltip: { type: String, default: '' },
    /**
     * @description Значение.
     * @type {number}
     */
    value: { required: true, type: [Number, String] },
    /**
     * @description Без встроенных элементов управления.
     */
    withoutNativeControls: {
      default: () => false,
      type: Boolean,
    },
    /**
     * @description Деактивирован.
     */
    disabled: {
      default: () => false,
      type: Boolean,
    },
    /**
     * @description Разрешена пустота.
     */
    allowedNull: {
      default: () => false,
      type: Boolean,
    },
    /**
     * @description Нарушение ограничений.
     * @type {string}
     */
    violations: { default: () => [], type: Array },
    /**
     * @description Маска
     * @type String
     */
    mask: { default: () => '', type: String },
    /**
     * @description Нативная проверка границ значения.
     * @type {boolean}
     */
    nativeMinMaxValidation: { default: false, type: Boolean },
  },
  watch: {
    value(newValue) {
      if (this.allowedNull && (newValue === '' || newValue === null)) {
        this.inputValue = '';
      } else {
        this.inputValue = newValue.toString();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";
@use "@/scss/fields";
.number {
  $this: &;
  display: flex;
  flex-direction: column;
  &__label {
    position: relative;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    margin: 0 0 2px 0;
    color: var(--unfocus-color);
    &:hover #{$this}__tooltip-hint {
      display: block
    }
  }
  &__tooltip {
    cursor: help;
    &::before {
      content: "?";
      display: inline-block;
      margin-left: 3px;
      font-size: 13px;
    }
    &-hint {
      position: absolute;
      bottom: 20px;
      left: 0;
      display: none;
      width: 360px;
      padding: 5px;
      font-size: 12px;
      color: rgba(0,0,0,.87);
      background-color: #ffffff;
      border: 1px solid #c2cfe0;
      border-radius: 5px;
      box-shadow: 0 0 20px rgba(54, 77, 92, 0.18);
      pointer-events: none;
      z-index: 2;
    }
  }
  &__input {
    @include fields.input-field();
    &.currency {
      padding-right: 32px;
      background: no-repeat right 10px center;
      background-image: url("@/assets/img/switch/tenge-grey.svg");
      background-size: 20px 20px;
    }
  }
  &__input.without-controls {
    appearance: textfield;
  }
  &__input.without-controls::-webkit-inner-spin-button,
  &__input.without-controls::-webkit-outer-spin-button {
    appearance: none;
  }
  &__validate-error {
    color: var(--warning-color);
    font-size: .8rem;
  }
}
</style>
